import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reservations, availabilityWindows, parseApiError, ApiErrorCode } from '@ooh/contracts';
import type { ReservationStatus, AvailabilityWindowState } from '@ooh/contracts';
import { toast } from '@ooh/ui/components/sonner';
import { MEDIA_UNIT_AVAILABILITY_KEY_PREFIX, reservationQueryKeys } from './queries';
import { invalidateReservationCaches } from './mutation-helpers';
import { useApiClient } from '../../plugs/api-client';
import { useActionBreadcrumb } from '../../plugs/action-breadcrumb';
import { withMutationBreadcrumbs } from '../../lib/mutation-breadcrumbs';
import { apiErrorToUserMessage } from '../../lib/api-error-messages';

// ─── Upgrade to HARD_RESERVATION ─────────────────────────────

interface UpgradeHardVars {
  id: number;
  expectedStatus: 'SOFT_RESERVATION';
  unitId?: number;
  /** CU-86can6mmu: true when acting on another user's reservation via reservation.force_release. */
  force?: boolean;
}

export function useUpgradeHard() {
  const api = useApiClient();
  const breadcrumb = useActionBreadcrumb();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, UpgradeHardVars>(
    withMutationBreadcrumbs<unknown, Error, UpgradeHardVars>(
      'reservation.upgradeHard',
      breadcrumb,
      {
        mutationFn: ({ id, expectedStatus, force }) =>
          api.call(reservations.upgradeHard, { id, expectedStatus, force }),
        onSuccess: (_data, { unitId }) => invalidateReservationCaches(queryClient, unitId),
      },
    ),
  );
}

// ─── Close sale (SOLD) ────────────────────────────────────────

interface CloseSaleVars {
  id: number;
  expectedStatus: 'SOFT_RESERVATION' | 'HARD_RESERVATION';
  unitId?: number;
  /** CU-86can6mmu: true when acting on another user's reservation via reservation.force_release. */
  force?: boolean;
}

export function useCloseSale() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, CloseSaleVars>({
    mutationFn: ({ id, expectedStatus, force }) =>
      api.call(reservations.closeSale, { id, expectedStatus, force }),
    onSuccess: (_data, { unitId }) => invalidateReservationCaches(queryClient, unitId),
  });
}

// ─── Cancel reservation ────────────────────────────────────────

interface CancelReservationVars {
  id: number;
  force?: boolean;
  unitId?: number;
}

export function useCancelReservation() {
  const api = useApiClient();
  const breadcrumb = useActionBreadcrumb();
  const queryClient = useQueryClient();

  // No hook-level onError here (deliberately) — `useReservationActions.dispatch`'s
  // 'cancel' branch supplies its own call-level onError (routes 409s to an inline
  // dialog message, see use-reservation-actions.ts), and TanStack Query fires BOTH
  // the hook-level onError AND a call-level one on every failure — a hook-level
  // toast here would double alongside that inline handling. Callers with no
  // call-level onError of their own (buildInspectQuickActionHandlers's onCancel)
  // supply one at the call site instead — see inventory-availability-shared.tsx.
  return useMutation<unknown, Error, CancelReservationVars>(
    withMutationBreadcrumbs<unknown, Error, CancelReservationVars>(
      'reservation.cancel',
      breadcrumb,
      {
        mutationFn: ({ id, force }) => api.call(reservations.cancel, { id, force: force ?? false }),
        onSuccess: (_data, { unitId }) => invalidateReservationCaches(queryClient, unitId),
      },
    ),
  );
}

// ─── Update reservation dates ─────────────────────────────────

interface UpdateReservationDatesVars {
  id: number;
  dateFrom: string;
  dateTo: string;
  expectedStatus: ReservationStatus;
  force?: boolean;
  unitId?: number;
}

export function useUpdateReservationDates() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, UpdateReservationDatesVars>({
    mutationFn: ({ id, dateFrom, dateTo, expectedStatus, force }) =>
      api.call(reservations.updateDates, {
        id,
        dateFrom: new Date(dateFrom),
        dateTo: new Date(dateTo),
        expectedStatus,
        force,
      }),
    onSuccess: (_data, { unitId }) => invalidateReservationCaches(queryClient, unitId),
  });
}

// ─── Override reservation status ──────────────────────────────

interface OverrideReservationStatusVars {
  id: number;
  targetStatus: ReservationStatus;
  reason: string;
  /** Optional manual TTL expiry override (ISO date string), honored for SOFT/HARD targets only. */
  ttlExpiresAt?: string;
  unitId?: number;
}

export function useOverrideReservationStatus() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, OverrideReservationStatusVars>({
    mutationFn: ({ id, targetStatus, reason, ttlExpiresAt }) =>
      api.call(reservations.override, {
        id,
        targetStatus,
        reason,
        ttlExpiresAt: ttlExpiresAt ? new Date(ttlExpiresAt) : undefined,
      }),
    onSuccess: (_data, { unitId }) => invalidateReservationCaches(queryClient, unitId),
  });
}

// ─── Manual effective-expiry edit (CU-86cb7qgc9) ──────────────

interface EditExpiryDeadlineVars {
  id: number;
  /** Date-only YYYY-MM-DD Warsaw calendar date. */
  expiryDate: string;
  reason: string;
  expectedStatus: 'SOFT_RESERVATION' | 'HARD_RESERVATION';
  unitId?: number;
}

/**
 * Dedicated typed mutation for `PATCH /reservations/:id/expiry-deadline`.
 * Reuses `invalidateReservationCaches` — global list, affected unit list,
 * and affected unit audit timeline — per the reservation-panel spec's
 * "Successful edit refreshes both reservation views and history" requirement.
 * Conflict/permission errors route through the existing translated
 * `handleMutationError` path (STALE_STATE is already in `INLINE_ERROR_CODES`
 * above; the caller's dialog stays open on error, matching every other
 * action in this file).
 */
export function useEditExpiryDeadline() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, EditExpiryDeadlineVars>({
    mutationFn: ({ id, expiryDate, reason, expectedStatus }) =>
      api.call(reservations.editExpiryDeadline, {
        id,
        expiryDate,
        reason,
        expectedStatus,
      }),
    onSuccess: (_data, { unitId }) => invalidateReservationCaches(queryClient, unitId),
  });
}

// ─── Force release ────────────────────────────────────────────
// Reuses reservations.cancel with force=true per spec.

interface ForceReleaseVars {
  id: number;
  unitId?: number;
}

export function useForceRelease() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, ForceReleaseVars>({
    mutationFn: ({ id }) => api.call(reservations.cancel, { id, force: true }),
    onSuccess: (_data, { unitId }) => invalidateReservationCaches(queryClient, unitId),
  });
}

// ─── Create availability window ("Zablokuj dostępność") ──────

interface CreateAvailabilityWindowVars {
  unitId: number;
  dateFrom: string;
  dateTo: string;
  state: AvailabilityWindowState;
}

/**
 * Maps a 409 RESERVATION_OVERLAPS_WINDOW error (thrown when a single
 * UNAVAILABLE window create/would overlap an existing active reservation) to
 * a translated toast; falls back to the raw backend message for every other
 * error code, matching the prior global-fallback behavior verbatim.
 */
export function handleCreateAvailabilityWindowError(
  error: unknown,
  t: (key: string) => string,
): void {
  const parsed = parseApiError(error);
  if (parsed.errorCode === ApiErrorCode.RESERVATION_OVERLAPS_WINDOW) {
    toast.error(t('toasts.reservationOverlapsWindow'));
    return;
  }
  toast.error(apiErrorToUserMessage(parsed, 'mutation'));
}

/**
 * Creates an availability window for a media unit.
 *
 * onError localizes 409 RESERVATION_OVERLAPS_WINDOW (see
 * handleCreateAvailabilityWindowError); every other error still surfaces the
 * raw backend message via toast, identical to what the global MutationCache
 * fallback previously did for this mutation. The caller (dialog) only closes
 * on success via its own per-call onSuccess — this onError never touches
 * that callback, so the dialog still stays open with the entered values
 * intact on any error, per the availability-windows spec.
 */
export function useCreateAvailabilityWindow() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation('reservations');

  return useMutation<unknown, Error, CreateAvailabilityWindowVars>({
    mutationFn: ({ unitId, dateFrom, dateTo, state }) =>
      api.call(availabilityWindows.create, {
        id: unitId,
        dateFrom: new Date(dateFrom),
        dateTo: new Date(dateTo),
        state,
      }),
    onSuccess: (_data, { unitId }) => {
      void queryClient.invalidateQueries({ queryKey: reservationQueryKeys.windowsByUnit(unitId) });
      void queryClient.invalidateQueries({ queryKey: reservationQueryKeys.byUnit(unitId) });
      void queryClient.invalidateQueries({ queryKey: MEDIA_UNIT_AVAILABILITY_KEY_PREFIX });
      // CU-86cak4cqy: also invalidate the global /reservations list — this is the
      // one mutation in this file that was missing it, requiring a manual page
      // refresh to see the effect after blocking availability from a reservation row.
      void queryClient.invalidateQueries({ queryKey: reservationQueryKeys.lists() });
    },
    onError: (error) => handleCreateAvailabilityWindowError(error, t as (key: string) => string),
  });
}
